import { useMemo } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { type Dayjs } from 'dayjs'
import { useOnboarding } from '../../context/OnboardingContext'
import { MISC_HANDLED_STATES, formatMiscHandledState } from '../../data/miscHandledStates'
import type { FilingsCurrentStatus } from '../../types/onboarding'
import { MISC_PRIMARY } from '../../theme/onboardingTheme'

const FILINGS_EXPLANATION_MAX = 1000

const selectAllSx = {
  borderColor: MISC_PRIMARY,
  color: MISC_PRIMARY,
  fontWeight: 600,
  fontSize: 14,
  px: 2,
  py: 1,
  '&:hover': { bgcolor: '#eff6ff', borderColor: MISC_PRIMARY },
}

const clearAllSx = {
  borderColor: '#cbd5e1',
  color: '#475569',
  fontWeight: 600,
  fontSize: 14,
  px: 2,
  py: 1,
  '&:hover': { bgcolor: '#f8fafc' },
}

const fieldSx = {
  mt: 1,
  maxWidth: 320,
  width: '100%',
  '& .MuiOutlinedInput-root': {
    fontSize: 14,
    bgcolor: '#ffffff',
    '& fieldset': { borderColor: '#cbd5e1' },
    '&:hover fieldset': { borderColor: MISC_PRIMARY },
    '&.Mui-focused fieldset': { borderColor: MISC_PRIMARY },
  },
}

export function StepGeneralQuestions() {
  const { state, dispatch } = useOnboarding()

  const selectedSummary = useMemo(
    () =>
      MISC_HANDLED_STATES.filter((s) => state.miscHandledStates.includes(s.code))
        .map((s) => formatMiscHandledState(s.code))
        .join(', '),
    [state.miscHandledStates],
  )

  const showExplanation = state.filingsCurrent === 'no'

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        bgcolor: '#ffffff',
        p: { xs: 3, sm: 4, lg: 5 },
        boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      }}
    >
      <Typography
        component="h3"
        sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' }, fontWeight: 600, color: '#0f172a' }}
      >
        General Questions
      </Typography>

      <Stack spacing={5} sx={{ mt: 4 }}>
        <Box component="section">
          <Typography sx={{ fontSize: { xs: 14, sm: 16 }, fontWeight: 600, color: '#0f172a' }}>
            What State(s) do you want MISC to handle?
          </Typography>

          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button
              type="button"
              variant="outlined"
              onClick={() =>
                dispatch({
                  type: 'SET_MISC_HANDLED_STATES',
                  codes: MISC_HANDLED_STATES.map((s) => s.code),
                })
              }
              sx={selectAllSx}
            >
              Select All States
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={() => dispatch({ type: 'SET_MISC_HANDLED_STATES', codes: [] })}
              sx={clearAllSx}
            >
              Clear All
            </Button>
          </Box>

          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
            }}
          >
            {MISC_HANDLED_STATES.map((st) => {
              const checked = state.miscHandledStates.includes(st.code)
              return (
                <Box
                  key={st.code}
                  component="label"
                  sx={{
                    display: 'flex',
                    cursor: 'pointer',
                    alignItems: 'center',
                    gap: 1,
                    borderRadius: 1,
                    border: '1px solid transparent',
                    py: 0.5,
                    pr: 1,
                    '&:hover': { bgcolor: '#f8fafc' },
                  }}
                >
                  <Checkbox
                    checked={checked}
                    onChange={() => dispatch({ type: 'TOGGLE_MISC_HANDLED_STATE', code: st.code })}
                    size="small"
                    sx={{
                      p: 0,
                      color: '#cbd5e1',
                      '&.Mui-checked': { color: MISC_PRIMARY },
                    }}
                  />
                  <Typography sx={{ fontSize: 14, color: '#1e293b' }}>
                    {st.code} - {st.name}
                  </Typography>
                </Box>
              )
            })}
          </Box>

          <Box
            sx={{
              mt: 2,
              borderRadius: 1,
              border: '1px solid #bfdbfe',
              bgcolor: '#eff6ff',
              px: 2,
              py: 1.5,
              fontSize: 14,
              color: '#1e293b',
            }}
          >
            <Typography component="span" sx={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>
              Selected States:{' '}
            </Typography>
            {selectedSummary || (
              <Typography component="span" sx={{ color: '#64748b', fontSize: 14 }}>
                None selected yet.
              </Typography>
            )}
          </Box>
        </Box>

        <Box component="section">
          <Typography sx={{ fontSize: { xs: 14, sm: 16 }, fontWeight: 600, color: '#0f172a' }}>
            What states are you currently writing or planning to write business in?
          </Typography>
          {state.miscHandledStates.length === 0 ? (
            <Typography sx={{ mt: 1.5, fontSize: 14, color: '#64748b' }}>
              Select one or more states in the previous question to choose where you write business.
            </Typography>
          ) : (
            <>
              <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() =>
                    dispatch({
                      type: 'SET_ACTIVE_BUSINESS_STATES',
                      codes: [...state.miscHandledStates],
                    })
                  }
                  sx={selectAllSx}
                >
                  Select All
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => dispatch({ type: 'SET_ACTIVE_BUSINESS_STATES', codes: [] })}
                  sx={clearAllSx}
                >
                  Clear All
                </Button>
              </Box>
              <Box
                sx={{
                  mt: 2,
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
                }}
              >
                {MISC_HANDLED_STATES.filter((st) => state.miscHandledStates.includes(st.code)).map(
                  (st) => {
                    const checked = state.activeBusinessStates.includes(st.code)
                    return (
                      <Box
                        key={st.code}
                        component="label"
                        sx={{
                          display: 'flex',
                          cursor: 'pointer',
                          alignItems: 'center',
                          gap: 1,
                          borderRadius: 1,
                          border: '1px solid transparent',
                          py: 0.5,
                          pr: 1,
                          '&:hover': { bgcolor: '#f8fafc' },
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          onChange={() =>
                            dispatch({ type: 'TOGGLE_ACTIVE_BUSINESS_STATE', code: st.code })
                          }
                          size="small"
                          sx={{
                            p: 0,
                            color: '#cbd5e1',
                            '&.Mui-checked': { color: MISC_PRIMARY },
                          }}
                        />
                        <Typography sx={{ fontSize: 14, color: '#1e293b' }}>
                          {st.code} - {st.name}
                        </Typography>
                      </Box>
                    )
                  },
                )}
              </Box>
            </>
          )}
        </Box>

        <Box component="section">
          <Typography
            component="label"
            htmlFor="transition-date"
            sx={{ display: 'block', fontSize: { xs: 14, sm: 16 }, fontWeight: 600, color: '#0f172a' }}
          >
            What date are you requesting to transition surplus lines tax filings and reporting to
            MISC?*
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              inputFormat="MM/DD/YYYY"
              value={state.transitionRequestDate ? dayjs(state.transitionRequestDate) : null}
              onChange={(value: Dayjs | null) =>
                dispatch({
                  type: 'SET_TRANSITION_REQUEST_DATE',
                  date: value && value.isValid() ? value.format('YYYY-MM-DD') : '',
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="transition-date"
                  size="small"
                  placeholder="MM/DD/YYYY"
                  sx={fieldSx}
                />
              )}
            />
          </LocalizationProvider>
          <Box
            component="ul"
            sx={{
              mt: 1.5,
              listStyle: 'none',
              p: 0,
              m: 0,
              fontSize: { xs: 12, sm: 14 },
              lineHeight: 1.625,
              color: '#475569',
            }}
          >
            <Box component="li">
              *We typically schedule all Go-Live dates at the start of a quarter or year (for
              example, 01/01/20XX or 04/01/20XX, etc.)
            </Box>
            <Box component="li">*This date must be agreed upon by MISC before your Go-Live</Box>
          </Box>
        </Box>

        <Box component="section">
          <Typography
            component="label"
            htmlFor="filings-current"
            sx={{ display: 'block', fontSize: { xs: 14, sm: 16 }, fontWeight: 600, color: '#0f172a' }}
          >
            Are you current on your surplus lines tax filings and reporting?
          </Typography>
          <FormControl size="small" sx={fieldSx}>
            <Select
              id="filings-current"
              value={state.filingsCurrent}
              displayEmpty
              onChange={(e) =>
                dispatch({
                  type: 'SET_FILINGS_CURRENT',
                  value: e.target.value as FilingsCurrentStatus,
                })
              }
            >
              <MenuItem value="">SELECT</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>

          {showExplanation ? (
            <Box sx={{ mt: 2 }}>
              <Typography
                component="label"
                htmlFor="filings-explain"
                sx={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#1e293b' }}
              >
                If no, please explain below
              </Typography>
              <TextField
                id="filings-explain"
                value={state.filingsExplanation}
                inputProps={{ maxLength: FILINGS_EXPLANATION_MAX }}
                multiline
                minRows={5}
                onChange={(e) =>
                  dispatch({ type: 'SET_FILINGS_EXPLANATION', text: e.target.value })
                }
                sx={{
                  mt: 1,
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    fontSize: 14,
                    '& fieldset': { borderColor: '#cbd5e1' },
                    '&:hover fieldset': { borderColor: MISC_PRIMARY },
                    '&.Mui-focused fieldset': { borderColor: MISC_PRIMARY },
                  },
                }}
              />
              <Typography sx={{ mt: 0.5, textAlign: 'right', fontSize: 12, color: '#64748b' }}>
                {state.filingsExplanation.length}/{FILINGS_EXPLANATION_MAX}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  )
}
